/**
 * openModal — one shared dialog-semantics + focus-management helper (POLISH_08 #3).
 *
 * Before this, every overlay (settings, feedback, language popup, pause menu, result
 * cards, skin/mutator/leaderboard panels) was a plain `<div class="sr-scrim">` with a
 * card inside: no role/aria-modal, no focus trap, no Escape handling, no focus restore.
 * A keyboard user tabbing through an open overlay could walk straight out into the
 * obscured page behind it, and had no way to dismiss a modal without a pointer.
 *
 * This helper does NOT build the scrim/card DOM (every call site already has its own
 * sticker-styled markup) — it just wires semantics + behavior onto an existing card:
 *   - role="dialog" + aria-modal="true" + aria-label on the card
 *   - remembers document.activeElement, focuses the first focusable control (or the
 *     card itself as a fallback) on open
 *   - traps Tab/Shift+Tab within the card (wraps at the ends)
 *   - Escape closes (via the returned close(), so callers keep a single dismiss path)
 *   - restores focus to whatever was focused before open() on close
 *
 * Escape coordination: the listener is registered on `window` with `capture: true` and
 * calls `stopPropagation()` before running `onClose`. main.ts's own window keydown
 * listener (pause toggle on P/Escape) is added later in the same tick via a NON-capture
 * listener, so a captured+stopped event never reaches it — no double-fire between "close
 * this modal" and "toggle pause" on the same Escape press. Gameplay pause behavior is
 * otherwise untouched: main.ts's Escape handling still owns pause/resume when no modal
 * from this helper is open.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface ModalHandle {
  /** Close the modal: removes the listeners this call installed + restores focus. Safe to
   *  call multiple times (a second call is a no-op). Does NOT remove the scrim/card from
   *  the DOM — callers still own their own teardown (e.g. `scrim.remove()`). */
  close: () => void;
}

export interface ModalOptions {
  /**
   * When true, skip stealing focus + trapping Tab on open (dialog role/aria-label/Escape
   * still apply). For a panel that intentionally coexists with live gameplay — e.g. the
   * in-run Leaderboard, toggled with L while the player keeps moving — auto-focusing a
   * button inside it would make Space (jump) click that button instead of playing, and
   * trapping Tab would strand a keyboard player who is also driving the character.
   * Default false (full modal: focus + trap), matching every blocking overlay.
   */
  nonBlocking?: boolean;
}

/**
 * Wire dialog semantics onto an already-built dialog card (the visible surface — a scrim
 * behind it, if any, is purely presentational and needs no attributes/listeners of its own).
 * @param card     the visible dialog surface — gets role/aria-modal/aria-label.
 * @param label    accessible name for the dialog (aria-label).
 * @param onClose  called once when Escape is pressed (or the caller may call close()
 *                 directly for a click-away/Cancel button — same restore-focus path).
 */
export function openModal(card: HTMLElement, label: string, onClose: () => void, options: ModalOptions = {}): ModalHandle {
  const { nonBlocking = false } = options;
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', nonBlocking ? 'false' : 'true');
  card.setAttribute('aria-label', label);
  if (!nonBlocking && !card.hasAttribute('tabindex')) card.setAttribute('tabindex', '-1');

  const previouslyFocused = document.activeElement as HTMLElement | null;

  const focusables = (): HTMLElement[] =>
    Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      .filter((el) => el.offsetParent !== null || el === document.activeElement);

  // Focus the first control (fields/buttons in source order); fall back to the card itself
  // so Tab still starts from a sane point even if a call site's card has no focusable child.
  // Skipped entirely in non-blocking mode — see ModalOptions.nonBlocking.
  if (!nonBlocking) {
    const first = focusables()[0];
    (first ?? card).focus();
  }

  let closed = false;
  const restoreAndClose = (): void => {
    if (closed) return;
    closed = true;
    window.removeEventListener('keydown', onKeydown, true);
    // Restore focus to whatever had it before open() — a keyboard user who opened the
    // modal via a button lands back on that same button, not the page top.
    if (!nonBlocking) previouslyFocused?.focus?.();
  };

  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation(); // captured ahead of main.ts's own (non-capture) Escape listener
      e.preventDefault();
      restoreAndClose();
      onClose();
      return;
    }
    if (nonBlocking || e.key !== 'Tab') return;
    const list = focusables();
    if (list.length === 0) { e.preventDefault(); return; }
    const activeIdx = list.indexOf(document.activeElement as HTMLElement);
    if (e.shiftKey) {
      if (activeIdx <= 0) { e.preventDefault(); list[list.length - 1].focus(); }
    } else {
      if (activeIdx === list.length - 1 || activeIdx === -1) { e.preventDefault(); list[0].focus(); }
    }
  };
  window.addEventListener('keydown', onKeydown, true); // capture: intercept before main.ts's Escape handler

  return { close: restoreAndClose };
}
